import axios from 'axios';

import * as api from './apiClient';
import { generateInstallationId } from './installationIdentity';
import * as storage from './storage';

/**
 * Site sessions for attribution. The server classifies them (direct / influenced / organic); the
 * site only says when one starts and whether a notification click started it.
 *
 * A session starts when the site opens and whenever the tab comes back after
 * `session_timeout_minutes` (brand config) hidden. Unlike an app's cold start, reloading the page
 * or opening another tab is not a new visit: the session and its last activity are kept in
 * `localStorage` and reused while they are inside the timeout.
 *
 * A visible tab waits a moment before starting a new session, because a notification click focuses
 * the tab first and says it was a click right after: that session has to be the direct one.
 */
const DEFAULT_TIMEOUT_MINUTES = 30;
const SETTLE_MS = 1500;
const SESSION_KEY = 'SESSION';

const state = {
  started: false,
  sessionId: null,
  startedAt: 0,
  hiddenAt: 0,
  timeoutMinutes: DEFAULT_TIMEOUT_MINUTES,
  pendingDirect: null,
  settleTimer: null,
  onSession: null,
};

const log = (...args) => console.log('[NotificationsV2][session]', ...args);

const timeoutMs = () => state.timeoutMinutes * 60 * 1000;

const touch = () => {
  if (state.sessionId) {
    storage.writeJson(SESSION_KEY, {
      session_id: state.sessionId,
      last_active_at: Date.now(),
    });
  }
};

const cancelSettle = () => {
  if (state.settleTimer) {
    clearTimeout(state.settleTimer);
    state.settleTimer = null;
  }
};

export const getSessionId = () => state.sessionId;

export const configure = ({
  session_timeout_minutes: minutes,
  onSession,
} = {}) => {
  if (Number.isFinite(Number(minutes)) && Number(minutes) > 0) {
    state.timeoutMinutes = Number(minutes);
  }
  if (onSession) {
    state.onSession = onSession;
  }
};

/** A click that should make the next session direct. Consumed by the next startSession. */
export const markDirectLaunch = deliveryId => {
  state.pendingDirect = deliveryId || null;
};

/**
 * Starts a session now. Idempotent per session id; the server answers the same classification
 * for a repeated id. Needs a session: the endpoint is behind the user JWT.
 */
export const startSession = async ({
  launch_type: launchType,
  direct_delivery_id: directDeliveryId,
} = {}) => {
  cancelSettle();

  if (!axios.defaults.headers.common.Authorization) {
    return { ok: false, skipped: 'no_session' };
  }

  const sessionId = generateInstallationId();
  const direct = directDeliveryId || state.pendingDirect || null;
  state.pendingDirect = null;
  state.sessionId = sessionId;
  state.startedAt = Date.now();
  touch();

  const body = {
    session_id: sessionId,
    launch_type: launchType || (direct ? 'notification_direct' : 'normal'),
    direct_delivery_id: direct || undefined,
    occurred_at: new Date().toISOString(),
  };

  try {
    const data = await api.postSession(body);
    log('session', body.launch_type, data?.attribution);
    if (state.onSession) {
      state.onSession({ session_id: sessionId, ...data });
    }
    return { ok: true, session_id: sessionId, ...data };
  } catch (err) {
    log('session start failed', api.refusalOf(err) || err?.message);
    return { ok: false, session_id: sessionId };
  }
};

/**
 * A notification click on a site that is already running starts its direct session now. Before
 * `start`, the click is kept so the site's first session is the direct one.
 */
export const startDirect = deliveryId => {
  markDirectLaunch(deliveryId);
  if (!state.started) {
    return null;
  }
  return startSession();
};

const handleVisibility = (visibility = document.visibilityState) => {
  const now = Date.now();

  if (visibility === 'hidden') {
    if (!state.hiddenAt) {
      state.hiddenAt = now;
    }
    touch();
    return;
  }

  if (visibility !== 'visible') {
    return;
  }

  const away = state.hiddenAt ? now - state.hiddenAt : 0;
  state.hiddenAt = 0;
  const expired = !state.sessionId || away > timeoutMs();

  if (!expired && !state.pendingDirect) {
    touch();
    return;
  }

  if (!state.settleTimer) {
    state.settleTimer = setTimeout(() => {
      state.settleTimer = null;
      startSession();
    }, SETTLE_MS);
  }
};

const onVisibilityChange = () => handleVisibility();

/** Site open: one session now (or the one still running), then visibility. Idempotent. */
export const start = () => {
  if (state.started) {
    return;
  }
  state.started = true;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', touch);

  const stored = storage.readJson(SESSION_KEY);
  const alive =
    stored?.session_id &&
    Number(stored.last_active_at) > 0 &&
    Date.now() - Number(stored.last_active_at) < timeoutMs();

  if (alive && !state.pendingDirect) {
    state.sessionId = stored.session_id;
    touch();
    return;
  }

  startSession();
};

export const stop = () => {
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('pagehide', touch);
  cancelSettle();
  Object.assign(state, {
    started: false,
    sessionId: null,
    startedAt: 0,
    hiddenAt: 0,
    pendingDirect: null,
  });
};

/** Logout: the next user starts a session of their own. */
export const forget = () => {
  cancelSettle();
  state.sessionId = null;
  storage.remove(SESSION_KEY);
};

/** Tests only. */
export const __handleVisibility = handleVisibility;
